import React, { memo, useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Col } from "antd";
import { Form } from "formik-antd";
import { formatForm } from "./newMapping";
import { mappingService, reportColumnTypeService } from "/app/src/services";
import {
  ColumnType,
  InternalIdType,
  TextType,
  AdvancedType,
  ReportColumnTypes,
  GroupingType,
  DraggableMappingType,
  ArrayType,
} from "./mappingTypes";
import {
  Integration,
  Mapping,
  MappingType,
  ReportColumnType,
} from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { themes } from "/app/src/constants/themes";
import getMappingTitle from "./utilities";
import {
  newMappingSchema,
  sqlColumnMappingSchema,
} from "/app/src/schemas/apps/dataPush/newMappingSchema";

interface FormValues {
  dataType: string | undefined;
  key: string | string[] | undefined;
  value: string | undefined;
  columnTypeId: number | [string | undefined, number | undefined] | undefined;
  type: MappingType | undefined;
  parentMappingId: number | undefined;
}

/**
 *
 * Component to edit the mapping
 */
const EditMapping = memo(function EditMapping({
  mapping,
  hideForm = false,
}: {
  mapping: Mapping & { connectionType?: string };
  hideForm: boolean;
}) {
  const queryClient = useQueryClient();
  const { integration } = queryClient.getQueryData<{
    integration: Integration;
  }>(["integration", mapping.integrationId]);
  const groupingLabel = getMappingTitle(mapping, integration);

  const isThemed = integration.baseTable !== null;

  const { data: columnTypes } = useQuery({
    queryKey: ["columnTypes", integration.baseTable],
    queryFn: () => {
      const params = { baseTable: integration.baseTable };
      if (integration.baseTable === "Material") {
        params["table"] = "Material";
      }
      if (params.baseTable === "Order" && integration.type === "delete") {
        params.baseTable = "Order Deletion";
      }
      return reportColumnTypeService.getAll(buildParams(params));
    },
    enabled: Boolean(
      themes.map((map) => map.value).includes(integration.baseTable) ||
        integration.baseTable === "Order",
    ),
    initialData: { report_column_types: [] },
    select: (data: { report_column_types: ReportColumnType[] }) => {
      return data.report_column_types;
    },
    staleTime: 60 * 60,
  });
  const { mutateAsync: updateMapping } = useMutation({
    mutationFn: (mapping: Omit<Mapping, "parentMapping" | "children">) => {
      return mappingService
        .updateSingle(mapping.id, mapping)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["mappings", integration.id],
        (oldData: { mappings: Mapping[] }) => {
          return {
            mappings: oldData.mappings.map((mapping) =>
              mapping.id === response.mapping.id ? response.mapping : mapping,
            ),
          };
        },
      );
    },
  });

  const { mutateAsync: removeMapping } = useMutation({
    mutationFn: (mapping: Mapping) => {
      return mappingService.deleteSingle(mapping.id).then(() => ({
        // Returns object with the mapping id
        mappingId: mapping.id,
      }));
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["mappings", integration.id],
        (oldData: { mappings: Mapping[] }) => {
          // Initialize a set to store the ids of mappings to remove
          const idsToRemove = new Set<number>();

          // Recursive function to find all descendants by parentMappingId
          const findDescendants = (mappings, parentId) => {
            // Loop through all mappings, if parentMappingId matched the parentId, add to the set and call the function recursively
            mappings.forEach((mapping) => {
              if (mapping.parentMappingId === parentId) {
                idsToRemove.add(mapping.id);
                findDescendants(mappings, mapping.id);
              }
            });
          };

          idsToRemove.add(response.mappingId);
          findDescendants(oldData.mappings, response.mappingId);

          // Array of mappings that are not in the set of ids to remove
          const remainingMappings = oldData.mappings.filter(
            (mapping) => !idsToRemove.has(mapping.id),
          );

          // Map that groups remaining mappings by parentMappingId
          const parentGroups = new Map<number | null, Mapping[]>();

          // Groups remaining mappings by parentMappingId. Each parentId maps to an array of its child mappings
          remainingMappings.forEach((mapping) => {
            const parentId = mapping.parentMappingId || null;
            if (!parentGroups.has(parentId)) {
              parentGroups.set(parentId, []);
            }
            parentGroups.get(parentId).push(mapping);
          });

          const updatedMappings = remainingMappings.map((mapping) => ({
            ...mapping,
          }));

          // Loop through each parent group and update the position of each mapping
          parentGroups.forEach((group) => {
            group.forEach((mapping, newIndex) => {
              const updatedMapping = updatedMappings.find(
                (m) => m.id === mapping.id,
              );
              const newPosition = String(newIndex);
              if (updatedMapping.position !== newPosition) {
                updatedMapping.position = newPosition;
                updateMapping({
                  id: updatedMapping.id,
                  parentMappingId: updatedMapping.parentMappingId,
                  position: newPosition,
                });
              }
            });
          });

          return {
            mappings: updatedMappings,
          };
        },
      );
    },
  });

  const updateMappingHandler = useCallback(
    async (values: FormValues) => {
      const formattedValues = formatForm(values);
      return await updateMapping({ id: mapping.id, ...formattedValues });
    },
    [mapping.id, updateMapping],
  );

  /**
   * Form for a column type mapping
   */
  const columnTypeForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => (
        <Form layout="vertical">
          <ColumnType
            integration={integration}
            isThemed={isThemed}
            columnTypes={columnTypes}
            removeMapping={removeMapping}
            mapping={mapping}
            dirty={dirty}
            isValid={isValid}
          />
        </Form>
      ),
      [columnTypes, integration, isThemed, mapping, removeMapping],
    );
  /**
   * Form for a text type mapping
   */
  const textTypeForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => {
        return (
          <Form>
            <TextType
              integration={integration}
              removeMapping={removeMapping}
              mapping={mapping}
              dirty={dirty}
              isValid={isValid}
            />
          </Form>
        );
      },
      [integration, mapping, removeMapping],
    );

  const booleanTypeForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => {
        return (
          <Form>
            <TextType
              removeMapping={removeMapping}
              mapping={mapping}
              dirty={dirty}
              isValid={isValid}
              isBoolean
            />
          </Form>
        );
      },
      [mapping, removeMapping],
    );

  const reportColumnForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => (
        <Form>
          <ReportColumnTypes
            removeMapping={removeMapping}
            mapping={mapping}
            dirty={dirty}
            isValid={isValid}
          />
        </Form>
      ),
      [mapping, removeMapping],
    );

  /**
   * For for an internal id mapping
   */
  const idTypeForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => (
        <Form>
          <InternalIdType
            removeMapping={removeMapping}
            mapping={mapping}
            dirty={dirty}
            isValid={isValid}
          />
        </Form>
      ),
      [mapping, removeMapping],
    );

  const advancedTypeForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => (
        <Form>
          <AdvancedType
            removeMapping={removeMapping}
            mapping={mapping}
            dirty={dirty}
            isValid={isValid}
            isThemed={isThemed}
            columnTypes={columnTypes}
          />
        </Form>
      ),
      [columnTypes, isThemed, mapping, removeMapping],
    );

  /**
   * Form for a grouping type mapping
   */
  const editGroupingForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form>
          <GroupingType
            label={groupingLabel}
            mapping={mapping}
            dirty={dirty}
            removeMapping={removeMapping}
          />
        </Form>
      ),
      [groupingLabel, mapping, removeMapping],
    );

  const arrayGroupingForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form>
          <ArrayType
            mapping={mapping}
            dirty={dirty}
            removeMapping={removeMapping}
          />
        </Form>
      ),
      [mapping, removeMapping],
    );

  /**
   * Get the initial values for the form
   */
  const getInitialValues = useCallback(() => {
    let columnType:
      | number
      | [string | undefined, number | undefined]
      | undefined = mapping.columnTypeId;
    if (isThemed) {
      const foundColumnType = columnTypes?.find(
        (ct) => ct.id === mapping.columnTypeId,
      );
      columnType = [foundColumnType?.table, foundColumnType?.id];
    }
    let value: string | undefined = mapping.value;
    // Backwards compatibility. Mappings used to have
    // Order and OrderLine as values for column types
    // This is no longer used, but we need to make sure
    // that these values are not passed to the form
    // in case regex patterns are being applied
    if (mapping.type === "column") {
      if (["Order", "OrderLine"].includes(mapping?.value)) {
        value = undefined;
      }
    }
    return {
      key: mapping.key,
      dataType: mapping.dataType,
      value: mapping.isPrivate ? "" : value,
      columnTypeId: columnType,
      parentMappingId: mapping.parentMappingId,
      type: mapping.type,
      isPrivate: mapping.isPrivate,
    } as {
      key: string | string[] | undefined;
      dataType: string | undefined;
      value: string | undefined;
      columnTypeId:
        | number
        | [string | undefined, number | undefined]
        | undefined;
      parentMappingId: number | undefined;
      type: MappingType | undefined;
      isPrivate: boolean | undefined;
    };
  }, [isThemed, mapping, columnTypes]);

  /**
   * Converts advanced type initial values to the correct format - list of strings
   */
  const getAdvancedInitialValues = () => {
    const initValues = getInitialValues();
    if (initValues.type === "advanced") {
      if (typeof initValues.key === "string") {
        initValues.key = initValues.key?.split(",");
      }
    }
    return initValues;
  };

  if (hideForm) {
    return <DraggableMappingType mapping={mapping} integration={integration} />;
  } else {
    return (
      <Col span={24}>
        {["lines", "grouping", "data"].includes(mapping.type) && (
          <Formik
            enableReinitialize
            component={editGroupingForm}
            initialValues={getInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "key", type: "string", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "array" && (
          <Formik
            enableReinitialize
            component={arrayGroupingForm}
            initialValues={getInitialValues()}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "column" && (
          <Formik
            enableReinitialize
            component={columnTypeForm}
            initialValues={getInitialValues()}
            validationSchema={
              ["MSSQL", "IBM2"].includes(integration.connectionType)
                ? sqlColumnMappingSchema
                : newMappingSchema
            }
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "boolean" && (
          <Formik
            enableReinitialize
            component={booleanTypeForm}
            initialValues={getInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "key", type: "string", required: true },
              { name: "value", type: "string", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "text" && (
          <Formik
            enableReinitialize
            component={textTypeForm}
            initialValues={getInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "key", type: "string", required: true },
              { name: "value", type: "string", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "id" && (
          <Formik
            enableReinitialize
            component={idTypeForm}
            initialValues={getInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "key", type: "string", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "reportColumn" && (
          <Formik
            enableReinitialize
            component={reportColumnForm}
            initialValues={getInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "dataType", type: "string", required: true },
              { name: "key", type: "string", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
        {mapping.type === "advanced" && (
          <Formik
            enableReinitialize
            component={advancedTypeForm}
            initialValues={getAdvancedInitialValues()}
            validationSchema={simpleSchemaBuilder([
              { name: "key", type: "mixed", required: true },
            ])}
            onSubmit={updateMappingHandler}
          />
        )}
      </Col>
    );
  }
});

export default EditMapping;
